// Token storage and JWT parsing.
// PKCE values go to sessionStorage (per-tab, short-lived).
// Tokens go to localStorage (persist across tabs/refreshes).

const PKCE_KEYS = ['code_verifier', 'auth_state'] as const;
type PkceKey = (typeof PKCE_KEYS)[number];
type TokenKey = 'access_token' | 'id_token' | 'refresh_token';
type StorageKey = PkceKey | TokenKey;

function isPkceKey(key: string): key is PkceKey {
  return (PKCE_KEYS as readonly string[]).includes(key);
}

export const tokenStorage = {
  set(key: StorageKey, value: string): void {
    if (isPkceKey(key)) sessionStorage.setItem(key, value);
    else localStorage.setItem(key, value);
  },

  get(key: StorageKey): string | null {
    if (isPkceKey(key)) return sessionStorage.getItem(key);
    return localStorage.getItem(key);
  },

  remove(key: StorageKey): void {
    if (isPkceKey(key)) sessionStorage.removeItem(key);
    else localStorage.removeItem(key);
  },

  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
  },
};

export interface JwtPayload {
  sub: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  roles?: string[];
  realm_access?: { roles: string[] };
  odoo_id?: number | string;
  exp: number;
  iat: number;
}

export function parseJwt(token: string): JwtPayload | null {
  try {
    const base64 = token
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function isTokenExpired(bufferMs = 30_000): boolean {
  const token = tokenStorage.get('access_token');
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload) return true;
  return payload.exp * 1000 < Date.now() + bufferMs;
}

export function getStoredTokens() {
  return {
    accessToken: tokenStorage.get('access_token'),
    idToken: tokenStorage.get('id_token'),
    refreshToken: tokenStorage.get('refresh_token'),
  };
}
